import { css } from '@emotion/react';
import styled from '@emotion/styled';
import * as React from 'react';
import { Transition } from 'react-transition-group';
// eslint-disable-next-line import/no-internal-modules
import type { TransitionStatus } from 'react-transition-group/Transition';
import { CategoryList, type CategoryListOption } from './CategoryList';
import type { MainCategory, Subcategory } from './types';

interface AnimatedCategorySelectorProps {
  categories: MainCategory[];
  onSubcategorySelected: (subcategory: Subcategory) => void;
  onBack: () => void;
  showBackOption: boolean;
  className?: string;
}

const transformDurationMs = 350;

function transitionDefinition({ durationMs }: { durationMs: number }) {
  return css`
    @media (prefers-reduced-motion: no-preference) {
      transition: transform ${durationMs}ms cubic-bezier(0.25, 0.1, 0.25, 1);
    }
  `;
}

function slidingTransitionStyles({
  status,
  startOffset,
  endOffset,
}: {
  status: TransitionStatus;
  startOffset: string;
  endOffset: string;
}) {
  const entering = css`
    transform: translateX(${startOffset});
  `;

  const exiting = css`
    transform: translateX(${endOffset});
  `;

  if (status === 'entering' || status === 'entered') {
    return entering;
  }

  return exiting;
}

const SlidingElement = styled.div<{
  status: TransitionStatus;
  durationMs: number;
  startOffset: string;
  endOffset: string;
}>`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: #fff;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.25);
  will-change: transform;

  ${transitionDefinition};
  ${slidingTransitionStyles};
`;

const backToSuggestionsOption: CategoryListOption = {
  icon: null,
  arrow: 'Left',
  label: 'Tilbake til våre forslag',
  description: null,
};

const backToMainCategoriesOption: CategoryListOption = {
  icon: null,
  arrow: 'Left',
  label: 'Tilbake til kategorier',
  description: null,
};

export const AnimatedCategorySelector: React.FC<
  AnimatedCategorySelectorProps
> = props => {
  const [selectedMainCategory, setSelectedMainCategory] =
    React.useState<MainCategory | null>(null);
  const [showMainCategories, setShowMainCategories] = React.useState(true);
  const { categories, onSubcategorySelected, showBackOption, onBack } = props;
  const nodeRef = React.useRef<HTMLDivElement>(null);
  const mainCategoryEleRef = React.useRef<HTMLDivElement>(null);
  const backButtonEleRef = React.useRef<HTMLButtonElement>(null);
  const [mainCategoryButtonEle, setMainCategoryButtonEle] =
    React.useState<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    backButtonEleRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div
      css={css`
        overflow: hidden;
        height: 400px;
        position: relative;
        contain: strict;
      `}
    >
      <Transition
        in={showMainCategories}
        timeout={transformDurationMs}
        nodeRef={nodeRef}
        onEntered={() => {
          mainCategoryButtonEle?.focus({ preventScroll: true });
        }}
        onExited={() => {
          // Safari has a bug where focusing an element inside the element with
          // subcategories makes the element flash or be displaced if the
          // element with main categories is a layer (in this case because of
          // will-change: transform). Turning off will-change just before
          // focusing and setting it back afterwards fixes the issue.
          const { current: ele } = mainCategoryEleRef;
          if (ele) {
            ele.style.willChange = 'auto';
            backButtonEleRef.current?.focus({ preventScroll: true });
            ele.style.willChange = '';
          }
        }}
      >
        {status => (
          <div ref={nodeRef}>
            <SlidingElement
              status={status}
              durationMs={transformDurationMs}
              startOffset="0"
              endOffset="-30%"
              ref={mainCategoryEleRef}
            >
              <CategoryList<MainCategory>
                specialOption={
                  showBackOption
                    ? {
                        option: {
                          ...backToSuggestionsOption,
                          isActive: showMainCategories,
                        },
                        onSelect: onBack,
                      }
                    : undefined
                }
                categories={categories}
                showArrow={true}
                onSelect={(selectedMainCategory, buttonEle) => {
                  setMainCategoryButtonEle(buttonEle);
                  setSelectedMainCategory(selectedMainCategory);
                  setShowMainCategories(false);
                }}
                isActive={showMainCategories}
                backButtonRef={backButtonEleRef}
                data-testid="main-categories"
              />
            </SlidingElement>
            <SlidingElement
              status={status}
              durationMs={transformDurationMs}
              startOffset="calc(100% + 10px)"
              endOffset="0"
            >
              {selectedMainCategory && (
                <CategoryList<Subcategory>
                  specialOption={{
                    option: {
                      ...backToMainCategoriesOption,
                      isActive: !showMainCategories,
                    },
                    onSelect: () => {
                      setShowMainCategories(true);
                    },
                  }}
                  categories={selectedMainCategory.items}
                  onSelect={onSubcategorySelected}
                  isActive={!showMainCategories}
                  backButtonRef={backButtonEleRef}
                  data-testid="subcategories"
                />
              )}
            </SlidingElement>
          </div>
        )}
      </Transition>
    </div>
  );
};
